///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Validation for the persisted stories slice. A story that is malformed
/// beyond repair (no id/title) is dropped; within a story, malformed
/// stops and annotations are dropped individually so one bad capture
/// never loses the whole story.
///

import { Constants } from "@/constants"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { CameraSavedState } from "@/types/CameraSavedState"
import {
	cMaxAnnotationLabelLength,
	cMaxAnnotationPoints,
	cMaxStopDwellSeconds,
	cMaxStopSourceUrls,
	cMaxStoryAnnotations,
	cMaxStoryBodyLength,
	cMaxStoryDatasets,
	cMaxStoryStops,
	cMaxStoryTitleLength,
	cDefaultStoryAnnotationColor,
	cStoryAnnotationColors,
	type StoriesState,
	type Story,
	type StoryAnnotation,
	type StoryDatasetRef,
	type StoryStop,
} from "../storiesSlice"

function isFiniteNumber(value: unknown): value is number {
	return typeof value === "number" && isFinite(value)
}

function isISODateString(value: unknown): value is string {
	return typeof value === "string" && !isNaN(Date.parse(value))
}

function isLatLon(value: unknown): value is { lat: number; lon: number } {
	if (!value || typeof value !== "object") return false
	const p = value as { lat?: unknown; lon?: unknown }
	return isFiniteNumber(p.lat) && Math.abs(p.lat) <= 90 &&
		isFiniteNumber(p.lon) && Math.abs(p.lon) <= 180
}

/** Validate an embedded camera pose; returns a clamped copy or null */
function validateCamera(camera: unknown): CameraSavedState | null {
	if (!camera || typeof camera !== "object") return null
	const c = camera as Partial<CameraSavedState>
	if (!isFiniteNumber(c.radius) || !isFiniteNumber(c.theta) ||
		!isFiniteNumber(c.phi) || !isFiniteNumber(c.tilt) ||
		!isFiniteNumber(c.bearing)) {
		return null
	}

	// Clamp radius to the valid camera altitude range (km, same as live camera)
	const minRadius = Constants.cMinCameraAltitude + wgs.a
	const maxRadius = Constants.cMaxCameraAltitude + wgs.a
	return {
		radius: Math.max(minRadius, Math.min(maxRadius, c.radius)),
		theta: c.theta,
		phi: c.phi,
		tilt: c.tilt,
		bearing: c.bearing,
	}
}

/** Validate one annotation; null drops it */
function validateAnnotation(annotation: unknown): StoryAnnotation | null {
	if (!annotation || typeof annotation !== "object") return null
	const a = annotation as { id?: unknown; kind?: unknown; label?: unknown; color?: unknown }

	if (typeof a.id !== "string" || a.id.length === 0) return null

	const label = typeof a.label === "string" && a.label.trim().length > 0
		? a.label.trim().slice(0, cMaxAnnotationLabelLength)
		: undefined
	const color = typeof a.color === "string" && cStoryAnnotationColors[a.color]
		? a.color
		: cDefaultStoryAnnotationColor

	if (a.kind === "marker") {
		const m = annotation as { lat?: unknown; lon?: unknown }
		if (!isLatLon({ lat: m.lat, lon: m.lon })) return null
		return { id: a.id, kind: "marker", lat: m.lat as number, lon: m.lon as number, label, color }
	}

	if (a.kind === "arc") {
		const arc = annotation as { points?: unknown; directional?: unknown }
		if (!Array.isArray(arc.points)) return null
		const points = arc.points.filter(isLatLon).slice(0, cMaxAnnotationPoints)
			.map(p => ({ lat: p.lat, lon: p.lon }))
		if (points.length < 2) return null
		return {
			id: a.id, kind: "arc", points, label, color,
			directional: arc.directional === true ? true : undefined,
		}
	}

	if (a.kind === "region") {
		const region = annotation as { ring?: unknown }
		if (!Array.isArray(region.ring)) return null
		const ring = region.ring.filter(isLatLon).slice(0, cMaxAnnotationPoints)
			.map(p => ({ lat: p.lat, lon: p.lon }))
		if (ring.length < 3) return null
		return { id: a.id, kind: "region", ring, label, color }
	}

	return null
}

/** Validate one cloud dataset ref; null drops it */
function validateDatasetRef(value: unknown): StoryDatasetRef | null {
	if (!value || typeof value !== "object") return null
	const d = value as Partial<StoryDatasetRef>

	if (typeof d.id !== "string" || d.id.length === 0) return null
	if (typeof d.name !== "string" || d.name.trim().length === 0) return null
	if (typeof d.filename !== "string" || d.filename.length === 0) return null
	if (d.format !== "geojson" && d.format !== "csv" && d.format !== "image") return null
	if (typeof d.cdnUrl !== "string" || !/^https?:\/\//.test(d.cdnUrl)) return null

	const ref: StoryDatasetRef = {
		id: d.id,
		name: d.name.trim().slice(0, cMaxStoryTitleLength),
		filename: d.filename.slice(0, 128),
		format: d.format,
		cdnUrl: d.cdnUrl,
		featureCount: isFiniteNumber(d.featureCount) && d.featureCount >= 0
			? Math.floor(d.featureCount) : undefined,
	}

	// Image ground overlays require a valid placement rectangle
	if (d.format === "image") {
		const bounds = validateRefBounds(d.bounds)
		if (!bounds) return null
		ref.bounds = bounds
		if (isFiniteNumber(d.opacity)) {
			ref.opacity = Math.max(0, Math.min(1, d.opacity))
		}
	}
	return ref
}

function validateRefBounds(value: unknown): StoryDatasetRef["bounds"] | null {
	if (!value || typeof value !== "object") return null
	const b = value as Record<string, unknown>
	const west = b.west, south = b.south, east = b.east, north = b.north
	if (!isFiniteNumber(west) || !isFiniteNumber(south) || !isFiniteNumber(east) || !isFiniteNumber(north)) return null
	if (west < -180 || east > 180 || south < -90 || north > 90) return null
	if (west >= east || south >= north) return null
	return { west, south, east, north }
}

/** Validate one stop; null drops it. knownAnnotationIds filters annotationIds. */
function validateStop(stop: unknown, knownAnnotationIds: Set<string>, knownDatasetIds: Set<string>): StoryStop | null {
	if (!stop || typeof stop !== "object") return null
	const s = stop as Partial<StoryStop>

	if (typeof s.id !== "string" || s.id.length === 0) return null

	const camera = validateCamera(s.camera)
	if (!camera) return null

	const dwellMode = s.dwell?.mode === "orbit" ? "orbit" : "hold"
	const dwellSeconds = isFiniteNumber(s.dwell?.seconds)
		? Math.max(0, Math.min(cMaxStopDwellSeconds, s.dwell.seconds))
		: undefined

	const layers = Array.isArray(s.layers)
		? s.layers.filter(l => typeof l === "string" && l.length > 0)
		: []
	const annotationIds = Array.isArray(s.annotationIds)
		? s.annotationIds.filter(id => typeof id === "string" && knownAnnotationIds.has(id))
		: []
	const datasetIds = Array.isArray(s.datasetIds)
		? s.datasetIds.filter(id => typeof id === "string" && knownDatasetIds.has(id))
		: []
	const sourceUrls = Array.isArray(s.sourceUrls)
		? s.sourceUrls.filter(url => typeof url === "string" && /^https?:\/\//.test(url)).slice(0, cMaxStopSourceUrls)
		: []

	return {
		id: s.id,
		camera,
		title: typeof s.title === "string" ? s.title.trim().slice(0, cMaxStoryTitleLength) : "",
		body: typeof s.body === "string" ? s.body.slice(0, cMaxStoryBodyLength) : "",
		focusTargetId: typeof s.focusTargetId === "string" && s.focusTargetId.length > 0
			? s.focusTargetId : undefined,
		layers: layers.length > 0 ? layers : undefined,
		dwell: { mode: dwellMode, seconds: dwellSeconds },
		annotationIds: annotationIds.length > 0 ? annotationIds : undefined,
		datasetIds: datasetIds.length > 0 ? datasetIds : undefined,
		sourceUrls: sourceUrls.length > 0 ? sourceUrls : undefined,
	}
}

/**
 * Validate a single story. Returns the (possibly clamped) story, or null
 * when it is malformed beyond repair and should be dropped. An empty
 * stops array is valid: it's a draft being authored.
 */
export function validateStory(story: unknown): Story | null {
	if (!story || typeof story !== "object") return null
	const s = story as Partial<Story>

	if (typeof s.id !== "string" || s.id.length === 0) return null
	if (typeof s.title !== "string" || s.title.trim().length === 0) return null

	const annotations: StoryAnnotation[] = []
	const annotationIds = new Set<string>()
	if (Array.isArray(s.annotations)) {
		for (const raw of s.annotations) {
			if (annotations.length >= cMaxStoryAnnotations) break
			const annotation = validateAnnotation(raw)
			if (!annotation || annotationIds.has(annotation.id)) continue
			annotations.push(annotation)
			annotationIds.add(annotation.id)
		}
	}

	const datasets: StoryDatasetRef[] = []
	const datasetIds = new Set<string>()
	if (Array.isArray(s.datasets)) {
		for (const raw of s.datasets) {
			if (datasets.length >= cMaxStoryDatasets) break
			const ref = validateDatasetRef(raw)
			if (!ref || datasetIds.has(ref.id)) continue
			datasets.push(ref)
			datasetIds.add(ref.id)
		}
	}

	const stops: StoryStop[] = []
	const stopIds = new Set<string>()
	if (Array.isArray(s.stops)) {
		for (const raw of s.stops) {
			if (stops.length >= cMaxStoryStops) break
			const stop = validateStop(raw, annotationIds, datasetIds)
			if (!stop || stopIds.has(stop.id)) continue
			stops.push(stop)
			stopIds.add(stop.id)
		}
	}

	// Prune pool datasets no surviving stop references
	const referencedDatasets = new Set(stops.flatMap(stop => stop.datasetIds ?? []))
	const prunedDatasets = datasets.filter(d => referencedDatasets.has(d.id))

	const createdAt = isISODateString(s.createdAt) ? s.createdAt : new Date().toISOString()
	const updatedAt = isISODateString(s.updatedAt) ? s.updatedAt : createdAt

	return {
		id: s.id,
		title: s.title.trim().slice(0, cMaxStoryTitleLength),
		stops,
		annotations,
		datasets: prunedDatasets,
		tags: Array.isArray(s.tags) ? s.tags.filter(t => typeof t === "string" && t.length > 0) : [],
		createdAt,
		updatedAt,
	}
}

/**
 * Validate the whole slice, dropping malformed stories and tombstones.
 */
export function validateStoriesState(state: unknown): StoriesState {
	const fallback: StoriesState = { stories: {}, deleted: {}, lastSyncedAt: null }
	if (!state || typeof state !== "object") return fallback
	const s = state as Partial<StoriesState>

	const stories: Record<string, Story> = {}
	if (s.stories && typeof s.stories === "object") {
		for (const [id, story] of Object.entries(s.stories)) {
			const validated = validateStory(story)
			if (validated?.id === id) {
				stories[id] = validated
			}
		}
	}

	const deleted: Record<string, string> = {}
	if (s.deleted && typeof s.deleted === "object") {
		for (const [id, deletedAt] of Object.entries(s.deleted)) {
			if (typeof id === "string" && id.length > 0 && isISODateString(deletedAt)) {
				deleted[id] = deletedAt
			}
		}
	}

	return {
		stories,
		deleted,
		lastSyncedAt: isISODateString(s.lastSyncedAt) ? s.lastSyncedAt : null,
	}
}
